/**
 * Sidebar/PinnedMenu/PinnedItem/index.tsx
 * desc: one pinned page — a row linking to it, with the unpin control that shows on hover or focus
 * note: the control sits beside the link, not inside it (a button inside a link is not valid markup); the row keeps
 *       room for it so a long label truncates before, not under, the control. The `child` variant is the text row
 *       inside the collapsed flyout — icon and label only, no unpin control there
 */

import { Link } from 'react-router';
import { UnpinIcon, type Icon } from '../../../Icon/typed';
import type { Pin } from '../../../../hooks/usePins';
import { classNames } from '../../../../utilities';
import { trackEvent } from '../../../../utilities/analytics';
import { translate } from '../../../../utilities/localization';

interface PinnedItemProps {
    pin: Pin;
    icon: Icon;
    isActive: boolean;
    // a profile write is in flight — the control waits for it
    isSaving?: boolean;
    onUnpin: (url: string) => void;
    variant?: 'row' | 'child';
}

const PinnedItem = ({ pin, icon: RowIcon, isActive, isSaving = false, onUnpin, variant = 'row' }: PinnedItemProps) => {
    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************
    const handleClick = () => trackEvent('Global: Sidebar - Pinned Link (Click)');

    const handleUnpinClick = () => {
        trackEvent('Global: Sidebar - Pinned Unpin (Click)');
        onUnpin(pin.url);
    };


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    return (
        <li className="pinned-item">
            <Link
                to={ pin.url }
                className={ classNames('row', { child: variant === 'child', selected: isActive }) }
                aria-current={ isActive ? 'page' : undefined }
                onClick={ handleClick }
            >
                <RowIcon readOnly tooltip={ null }/>
                <span className="label">{ pin.label }</span>
            </Link>
            { variant === 'row' ? <UnpinIcon className="unpin" disabled={ isSaving } onClick={ handleUnpinClick } tooltip={ translate('nav.pinned.unpin', { label: pin.label }) }/> : null }
        </li>
    );
};

export default PinnedItem;
